// src/utils.ts - ESLint compliant version with enhanced formatting
import type { Contact } from './types';

export const getInitials = (firstName?: string, lastName?: string): string => {
  const safeFirst = typeof firstName === 'string' ? firstName.trim() : '';
  const safeLast = typeof lastName === 'string' ? lastName.trim() : '';
  
  const first = safeFirst.charAt(0).toUpperCase() || '';
  const last = safeLast.charAt(0).toUpperCase() || '';
  
  return (first + last) || '?';
};

export const formatDate = (dateString?: string): string => {
  if (!dateString || typeof dateString !== 'string') return 'Unknown';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString();
  } catch {
    return 'Invalid date';
  }
};

// ✅ ENHANCED: Premium number formatting with proper comma handling
export const formatLargeNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(/\.?0+$/, '')}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1).replace(/\.?0+$/, '')}K`;
  }
  // ✅ FIXED: Add commas to smaller numbers too
  return num.toLocaleString();
};

// ✅ ENHANCED: Smart time formatting for search results
export const formatTime = (ms: number): string => {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${ms}ms`;
};

export interface ValidationStatus {
  text: string;
  className: string;
}

export const getValidationStatus = (status?: string | null): ValidationStatus => {
  if (!status || typeof status !== 'string') {
    return { text: 'Unverified', className: 'status-unverified' };
  }
  
  const normalizedStatus = status.toLowerCase().trim();
  
  if (normalizedStatus === 'valid' || normalizedStatus === 'verified') {
    return { text: 'Verified', className: 'status-verified' };
  } else if (normalizedStatus === 'invalid') {
    return { text: 'Invalid', className: 'status-invalid' };
  }
  
  return { text: 'Unverified', className: 'status-unverified' };
};

export const formatNullableValue = (value?: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  
  const stringValue = String(value).trim();
  return stringValue || '—';
};

export const formatCompanySize = (size?: string): string => {
  if (!size || typeof size !== 'string' || size === '—') return '—';
  
  const trimmedSize = size.trim();
  
  if (trimmedSize.includes('-')) {
    return trimmedSize;
  }
  
  const num = parseInt(trimmedSize, 10);
  if (!isNaN(num)) {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M+ employees`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K+ employees`;
    } else {
      return `${num} employees`;
    }
  }
  
  return trimmedSize;
};

export const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
};

export const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Contact validation helper
export const validateContact = (contact: unknown): contact is Contact => {
  return contact !== null && 
         typeof contact === 'object' && 
         !Array.isArray(contact) &&
         Object.keys(contact as Record<string, unknown>).length > 0;
};

// Safe array validation
export const validateArray = <T>(arr: unknown, validator?: (item: unknown) => item is T): T[] => {
  if (!Array.isArray(arr)) {
    console.warn('Expected array, got:', typeof arr);
    return [];
  }
  
  if (validator) {
    return arr.filter(validator);
  }
  
  return arr as T[];
};